import { createListenerMiddleware } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import { addAppErrorMessage } from 'modules/alerts/state';

import { getOpenNatterChannels } from '../constants/channels';
import { JOIN_RETRY_CONFIG, MARK_READY_CONFIG } from '../constants/websocket';
import { openNatterFeature } from '../OpenNatterFeature';
import { selfLeftEventAction, selfMatchedAction, selfUnmatchedAction } from '../socket/openNatterMessageDispatcher';
import { sequenceChecker } from '../socket/sequenceChecker';
import { OpenNatterPhase } from '../types/state';
import { natterActions } from './openNatter.actions';
import {
  connectSocket,
  countdownEnded,
  leaveOpenNatter,
  markReady,
  subscribeToOpenNatter,
  unsubscribeFromOpenNatter,
} from './openNatter.actions';
import { selectOpenNatterId, selectOpenNatterPhase } from './openNatter.selectors';
import { markReadyTimeout } from './openNatter.slice';

import type { ListenerEffectAPI, TypedStartListening } from '@reduxjs/toolkit';
import type { CombinedDispatch, RootState } from 'store';

/**
 * Type definitions for listener middleware
 */
export type AppStartListening = TypedStartListening<RootState, CombinedDispatch>;

export const openNatterListener = createListenerMiddleware();

const startAppListening = openNatterListener.startListening as AppStartListening;

/**
 * Helper function to wait for phase change with timeout
 * Used to handle race conditions between API calls and WebSocket messages
 */
const waitForPhaseChange = async (
  listenerApi: ListenerEffectAPI<RootState, CombinedDispatch>,
  fromPhase: OpenNatterPhase,
  timeoutMs: number = MARK_READY_CONFIG.TIMEOUT_MS
): Promise<void> => {
  const timeout = new Promise<void>((_, reject) => {
    setTimeout(() => {
      const currentPhase = selectOpenNatterPhase(listenerApi.getState());
      if (currentPhase === fromPhase) {
        reject(new Error(`Phase did not change from ${fromPhase} after ${timeoutMs}ms`));
      }
    }, timeoutMs);
  });

  const phaseChange = listenerApi.condition((_action, currentState, previousState) => {
    const prevPhase = selectOpenNatterPhase(previousState);
    const currPhase = selectOpenNatterPhase(currentState);
    return prevPhase === fromPhase && currPhase !== fromPhase;
  });

  await Promise.race([phaseChange, timeout]);
};

/**
 * Listener: Connect to WebSocket server
 * Establishes WebSocket connection without subscribing to any channels
 * Used to pre-connect before user clicks Join
 */
startAppListening({
  actionCreator: connectSocket,
  effect: async (_action, listenerApi) => {
    try {
      const socket = openNatterFeature.getSocket();

      // Connect socket if not already connected
      if (!socket.isConnected()) {
        logger.info('[OpenNatter Listener] Connecting to WebSocket');
        socket.connect();
      } else {
        logger.debug('[OpenNatter Listener] Socket already connected');
      }
    } catch (error) {
      logger.error('[OpenNatter Listener] Failed to connect to WebSocket', { error });
      listenerApi.dispatch(
        addAppErrorMessage('Unable to connect to Open Natter. Please check your connection and try again.')
      );
    }
  },
});

/**
 * Listener: Subscribe to WebSocket channels
 * Resets state first to clear any stale data from previous sessions
 */
startAppListening({
  actionCreator: subscribeToOpenNatter,
  effect: async (action, listenerApi) => {
    const { id } = action.payload;

    try {
      const channels = getOpenNatterChannels(id);

      const socket = openNatterFeature.getSocket();

      // Connect socket if not connected
      if (!socket.isConnected()) {
        socket.connect();
      }

      // Subscribe to channels
      socket.subscribe(channels);

      logger.info('[OpenNatter Listener] Subscribed to channels', { id, channels });
    } catch (error) {
      logger.error('[OpenNatter Listener] Failed to subscribe to channels', { error, id });
      listenerApi.dispatch(
        addAppErrorMessage('Unable to connect to Open Natter. Please check your connection and try again.')
      );
    }
  },
});

/**
 * Listener: Unsubscribe from WebSocket channels
 * Note: Does NOT reset state - that should happen when starting a new session
 * This allows the Ended phase to persist for the rating screen
 */
startAppListening({
  actionCreator: unsubscribeFromOpenNatter,
  effect: async (action, _listenerApi) => {
    const { id } = action.payload;

    try {
      const channels = getOpenNatterChannels(id);
      const socket = openNatterFeature.getSocket();

      // Unsubscribe from channels
      socket.unsubscribe(channels);

      // Clear sequence tracking for this event (prevents stale data on rejoin)
      sequenceChecker.reset(id);

      logger.info('[OpenNatter Listener] Unsubscribed from channels', { id, channels });
    } catch (error) {
      logger.error('[OpenNatter Listener] Failed to unsubscribe from channels', { error, id });
    }
  },
});

/**
 * Listener: Join Open Natter with timeout and retry logic
 * Waits for the phase to change from Idle after subscribing to WebSocket
 * Retries up to 3 times with progressive timeouts: 5s, 10s, 15s
 */
startAppListening({
  actionCreator: natterActions.joinNatterClicked.fulfilled,
  effect: async (action, listenerApi) => {
    for (let attempt = 1; attempt <= JOIN_RETRY_CONFIG.MAX_ATTEMPTS; attempt++) {
      const timeoutMs = JOIN_RETRY_CONFIG.BASE_TIMEOUT_MS * attempt; // 5s, 10s, 15s

      try {
        logger.info('[OpenNatter Listener] Waiting for join snapshot', { attempt, timeoutMs });

        // Wait for phase to change from Idle (triggered by SelfJoinedWaitingRoom or other snapshot message)
        await waitForPhaseChange(listenerApi, OpenNatterPhase.Idle, timeoutMs);

        logger.info('[OpenNatter Listener] Successfully joined Open Natter - snapshot received', { attempt });
        return; // Success - exit the retry loop
      } catch (error) {
        logger.info('[OpenNatter Listener] Timeout waiting for join snapshot', { attempt, timeoutMs, error });

        // Check if phase changed during this attempt (race condition - success)
        const currentPhase = selectOpenNatterPhase(listenerApi.getState() as RootState);
        if (currentPhase !== OpenNatterPhase.Idle) {
          logger.info('[OpenNatter Listener] Phase changed during timeout - join successful', {
            attempt,
            currentPhase,
          });
          return; // Success - phase changed
        }

        if (attempt < JOIN_RETRY_CONFIG.MAX_ATTEMPTS) {
          // Retry: unsubscribe and resubscribe
          const id = selectOpenNatterId(listenerApi.getState() as RootState);
          if (id) {
            logger.info('[OpenNatter Listener] Retrying join - unsubscribe/resubscribe', {
              attempt,
              nextAttempt: attempt + 1,
            });
            listenerApi.dispatch(unsubscribeFromOpenNatter({ id }));

            // Small delay before resubscribing
            await listenerApi.delay(JOIN_RETRY_CONFIG.RETRY_DELAY_MS);

            listenerApi.dispatch(subscribeToOpenNatter({ id }));
          }
        }
      }
    }

    // All attempts failed
    logger.error('[OpenNatter Listener] All join attempts failed after retries');
    listenerApi.dispatch(
      addAppErrorMessage('Connection timed out while joining Open Natter. Please refresh and try again.')
    );

    // Unsubscribe on final failure
    const id = selectOpenNatterId(listenerApi.getState() as RootState);
    if (id) {
      listenerApi.dispatch(unsubscribeFromOpenNatter({ id }));
    }
  },
});

/**
 * Listener: Mark ready with timeout for phase change
 * Waits for the phase to change from Waiting after the API call
 * Timeout: 10 seconds - shows error and resets loading state
 */
startAppListening({
  actionCreator: markReady.fulfilled,
  effect: async (action, listenerApi) => {
    try {
      // Wait for phase to change from Waiting (triggered by SelfJoinedWaitingRoom with ready=true)
      await waitForPhaseChange(listenerApi, OpenNatterPhase.Waiting, MARK_READY_CONFIG.TIMEOUT_MS);

      logger.info('[OpenNatter Listener] Successfully marked ready');
    } catch (error) {
      logger.error('[OpenNatter Listener] Timeout waiting for ready confirmation', { error });

      // Reset loading state and show error - user can try again
      listenerApi.dispatch(markReadyTimeout());
      listenerApi.dispatch(addAppErrorMessage('Ready status not confirmed. Please try again.'));
    }
  },
});

/**
 * Listener: Leave Open Natter API call completed
 * Logs successful leave - unsubscription is handled by SelfLeftEvent listener
 */
startAppListening({
  actionCreator: leaveOpenNatter.fulfilled,
  effect: async (action, _listenerApi) => {
    const { id } = action.payload;
    logger.info('[OpenNatter Listener] Successfully left Open Natter', { id });
  },
});

/**
 * Listener: SelfLeftEvent received
 * Unsubscribes from channels when natter ends (regardless of how it ended)
 * This handles both user-initiated leave and server-initiated end (timeout)
 */
startAppListening({
  actionCreator: selfLeftEventAction,
  effect: async (action, listenerApi) => {
    const id = action.payload.openNatterId.value;

    // Unsubscribe from channels
    listenerApi.dispatch(unsubscribeFromOpenNatter({ id }));

    logger.info('[OpenNatter Listener] SelfLeftEvent received - unsubscribed from channels', { id });
  },
});

/**
 * Listener: Countdown ended
 * Handles when the UI countdown timer reaches zero
 */
startAppListening({
  actionCreator: countdownEnded,
  effect: async (_action, listenerApi) => {
    const state = listenerApi.getState() as RootState;

    // Transition to appropriate phase based on starting soon state
    if (state.openNatter.startingSoon === 'matched') {
      // Already should be in Matched phase from SelfMatched message
      logger.info('[OpenNatter Listener] Countdown ended for matched room');
    } else if (state.openNatter.startingSoon === 'unmatched') {
      // Already should be in Unmatched phase from SelfUnmatched message
      logger.info('[OpenNatter Listener] Countdown ended for unmatched session');
    }
  },
});

/**
 * Listener: SelfMatched - wait for startsAt before transitioning to room
 * Dispatches userMatched thunk which waits until startsAt time
 * Uses serverNow to calculate delay independent of client clock skew
 * If startsAt is in the past (rejoin), the slice already set the phase, so we skip
 */
startAppListening({
  actionCreator: selfMatchedAction,
  effect: async (action, listenerApi) => {
    const { startsAt, serverNow } = action.payload;
    const startsAtTime = new Date(startsAt).getTime();
    const serverNowTime = new Date(serverNow).getTime();

    // If startsAt is in the past (server time), the slice already transitioned the phase
    if (startsAtTime <= serverNowTime) {
      logger.info('[OpenNatter Listener] Matched - startsAt already passed, phase set by reducer');
      return;
    }

    const delayMs = startsAtTime - serverNowTime;
    logger.info('[OpenNatter Listener] Matched - waiting for room start', { startsAt, serverNow, delayMs });

    // Wait until startsAt before transitioning phase to Matched (using server time)
    await listenerApi.dispatch(natterActions.userMatched({ startsAt, serverNow })).unwrap();

    logger.info('[OpenNatter Listener] Room start time reached, transitioning to Matched');
  },
});

/**
 * Listener: SelfUnmatched - wait for startsAt before transitioning to unmatched session
 * Dispatches userUnmatched thunk which waits until startsAt time
 * Uses serverNow to calculate delay independent of client clock skew
 * If startsAt is in the past (rejoin), the slice already set the phase, so we skip
 */
startAppListening({
  actionCreator: selfUnmatchedAction,
  effect: async (action, listenerApi) => {
    const { startsAt, serverNow } = action.payload;
    const startsAtTime = new Date(startsAt).getTime();
    const serverNowTime = new Date(serverNow).getTime();

    // If startsAt is in the past (server time), the slice already transitioned the phase
    if (startsAtTime <= serverNowTime) {
      logger.info('[OpenNatter Listener] Unmatched - startsAt already passed, phase set by reducer');
      return;
    }

    const delayMs = startsAtTime - serverNowTime;
    logger.info('[OpenNatter Listener] Unmatched - waiting for session start', { startsAt, serverNow, delayMs });

    // Wait until startsAt before transitioning phase to Unmatched (using server time)
    await listenerApi.dispatch(natterActions.userUnmatched({ startsAt, serverNow })).unwrap();

    logger.info('[OpenNatter Listener] Session start time reached, transitioning to Unmatched');
  },
});
